<template>
  <b-container>
    <div class="auth-complete">
      <h1 class="auth-complete__title">Signing In</h1>
      <div v-if="!error" class="auth-complete__loading">
        <Spinner />
        <p class="auth-complete__loading-text">
          {{ providerLabel }}
        </p>
      </div>
      <div v-else class="auth-complete__error">
        <p class="auth-complete__error-message">{{ error }}</p>
        <b-nav-item to="/sign-in" class="auth-complete__link"> Return to Sign In </b-nav-item>
      </div>
    </div>
  </b-container>
</template>

<script>
import { setItemsToLocalStorage } from '@/services/functions';
import Spinner from '@/components/Spinner';
import { AuthMethodEnum, AUTH_METHOD_STORAGE_KEY } from '@/utils/constants/common';

export default {
  name: 'AuthComplete',
  components: {
    Spinner,
  },
  data() {
    return {
      error: null,
      provider: null,
    };
  },
  computed: {
    providerLabel() {
      if (this.provider === 'google') {
        return 'Completing authentication with Google...';
      }
      if (this.provider === 'entra') {
        return 'Completing authentication with Microsoft Entra ID...';
      }
      return 'Completing sign in...';
    },
  },
  mounted() {
    this.handleAuthComplete();
  },
  methods: {
    handleAuthComplete() {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(
          window.location.hash ? window.location.hash.slice(1) : ''
        );
        const success = searchParams.get('success') || hashParams.get('success');
        const provider = searchParams.get('provider') || hashParams.get('provider');
        const error = searchParams.get('error') || hashParams.get('error');

        this.provider = provider || null;
        const errorMessage = searchParams.get('message') || hashParams.get('message');

        if (error) {
          this.error = errorMessage || 'Authentication failed';
          return;
        }

        if (!success) {
          this.error = 'Authentication failed: Invalid callback';
          return;
        }

        setItemsToLocalStorage([[AUTH_METHOD_STORAGE_KEY, AuthMethodEnum.SSO]]);

        this.$auth.isAuthenticated = true;

        window.history.replaceState(null, '', window.location.pathname);

        setTimeout(() => {
          this.$router.push('/integrations');
        }, 100);
      } catch (err) {
        console.error('Auth complete error:', err);
        this.error = 'Authentication failed: ' + err.message;
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/css/fonts';

$footer-height-default: 192px;
$footer-height-1440: 251px;
$footer-height-594: 331px;

.auth-complete {
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  min-height: 550px;

  &__title {
    @include Aspekta-font(700, 60px, normal, #242424);
    font-size: calc(31px + (60 - 31) * calc((100vw - 320px) / (1920 - 320)));
    letter-spacing: 0em;
    margin: 36px 0;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    margin-top: 60px;
  }

  &__loading-text {
    @include Aspekta-font(500, 18px, normal, #706969);
    margin: 0;
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 60px;
    gap: 30px;
  }

  &__error-message {
    @include Aspekta-font(500, 18px, normal, #fb8d62);
    text-align: center;
    margin: 0;
    max-width: 500px;
    white-space: pre-line;
  }

  &__link {
    display: inline-block;
    @include Aspekta-font(700, 18px, normal, transparent);

    a {
      color: #ffb400;
      font-size: 18px !important;

      &:hover {
        text-decoration: underline;
        color: #ff7a08;
      }
    }
  }
}

@media (max-width: 768px) {
  .auth-complete {
    padding: 0 20px;

    &__title {
      margin: 30px 0;
    }

    &__loading {
      margin-top: 40px;
    }

    &__error {
      margin-top: 40px;
    }
  }
}
</style>
